"use strict";
/**
 * Blockchain bridges that connects the SDK and the KILT Blockchain.
 *
 * Communicates with the chain via WebSockets and can [[listenToBlocks]]. It exposes the [[signTx]] function that performs the necessary tx signing.
 *
 * @packageDocumentation
 * @module Blockchain
 */
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const bn_js_1 = tslib_1.__importDefault(require("bn.js"));
const utils_1 = require("@kiltprotocol/utils");
const config_1 = require("@kiltprotocol/config");
const Blockchain_utils_1 = require("./Blockchain.utils");
const log = config_1.ConfigService.LoggingFactory.getLogger('Blockchain');
// Code taken from
// https://polkadot.js.org/api/api/classes/_promise_index_.apipromise.html
class Blockchain {
    constructor(api) {
        this.api = api;
        this.accountNonces = new Map();
    }
    static asArray(queryResult) {
        const json = queryResult.toJSON();
        if (json instanceof Array)
            return json;
        return [];
    }
    async getStats() {
        const encoded = await Promise.all([
            this.api.rpc.system.chain(),
            this.api.rpc.system.name(),
            this.api.rpc.system.version(),
        ]);
        const [chain, nodeName, nodeVersion] = encoded.map((el) => el.toString());
        return { chain, nodeName, nodeVersion };
    }
    // TODO: implement unsubscribe as subscriptionId continuously increases
    async listenToBlocks(listener) {
        return this.api.rpc.chain.subscribeNewHeads(listener);
    }
    /**
     * [ASYNC] Signs the SubmittableExtrinsic with the given identity.
     *
     * @param identity The [[Identity]] to sign the tx with.
     * @param tx The unsigned SubmittableExtrinsic.
     * @param tip The amount of Femto-KILT to tip the validator.
     * @returns Signed SubmittableExtrinsic.
     *
     */
    async signTx(identity, tx, tip) {
        const nonce = await this.getNonce(identity.address);
        const signed = await identity.signSubmittableExtrinsic(tx, nonce, tip);
        return signed;
    }
    /**
     * [ASYNC] Submits a signed SubmittableExtrinsic with imported function [[submitSignedTx]].
     * Handles recoverable errors if identity is provided by re-signing and re-sending the tx up to two times.
     * Uses [[parseSubscriptionOptions]] to provide complete potentially defaulted options to the called [[submitSignedTx]].
     *
     * Transaction fees will apply whenever a transaction fee makes it into a block, even if extrinsics fail to execute correctly!
     *
     * @param tx The SubmittableExtrinsic to be submitted. Most transactions need to be signed, this must be done beforehand.
     * @param identity Optional [[Identity]] to potentially re-sign the tx with.
     * @param opts Optional partial criteria for resolving/rejecting the promise.
     * @returns A promise which can be used to track transaction status.
     * If resolved, this promise returns the eventually resolved ISubmittableResult.
     */
    async submitSignedTxWithReSign(tx, identity, opts) {
        const retry = async (reason) => {
            if (reason.message === utils_1.SDKErrors.ERROR_TRANSACTION_RECOVERABLE().message &&
                identity) {
                return Blockchain_utils_1.submitSignedTx(await this.reSignTx(identity, tx), opts);
            }
            throw reason;
        };
        return Blockchain_utils_1.submitSignedTx(tx, opts).catch(retry).catch(retry);
    }
    /**
     * [ASYNC] Retrieves the Nonce for Transaction signing for the specified account and increments the in accountNonces mapped Index.
     *
     * @param accountAddress The address of the identity that we retrieve the nonce for.
     * @returns Representation of the Tx nonce for the identity.
     *
     */
    async getNonce(accountAddress) {
        let nonce = this.accountNonces.get(accountAddress);
        if (!nonce) {
            // the account nonce is unknown, we will query it from chain
            const chainNonce = await this.api.rpc.system
                .accountNextIndex(accountAddress)
                .catch((reason) => {
                log.error(`On-chain nonce retrieval failed for account ${accountAddress}\nwith reason: ${reason}`);
                throw Error(`Chain failed to retrieve nonce for : ${accountAddress}`);
            });
            // ensure that the nonce we queried is still up to date and no newer nonce was queried during the await above
            const secondQuery = this.accountNonces.get(accountAddress);
            nonce = bn_js_1.default.max(chainNonce, secondQuery || new bn_js_1.default(0));
        }
        this.accountNonces.set(accountAddress, nonce.addn(1));
        return nonce;
    }
    /**
     * [ASYNC] Re-signs the given SubmittableExtrinsic with an updated Nonce.
     *
     * @param identity The [[Identity]] to re-sign the Tx with.
     * @param tx The tx with recoverable Error that failed.
     * @returns Original Tx, injected with signature payload with updated nonce.
     *
     */
    async reSignTx(identity, tx) {
        this.accountNonces.delete(identity.address);
        const nonce = await this.getNonce(identity.address);
        const signerPayload = this.api
            .createType('SignerPayload', {
            method: tx.method.toHex(),
            nonce,
            genesisHash: this.api.genesisHash,
            blockHash: this.api.genesisHash,
            runtimeVersion: this.api.runtimeVersion,
            version: this.api.extrinsicVersion,
        })
            .toPayload();
        tx.addSignature(identity.address, this.api
            .createType('ExtrinsicPayload', signerPayload, {
            version: this.api.extrinsicVersion,
        })
            .sign(identity.signKeyringPair).signature, signerPayload);
        return tx;
    }
}
exports.default = Blockchain;
